import { useState, useEffect } from 'react'
import { Stack, Title, Card, Table, Badge, Button, Group, Text, Progress, ActionIcon, Menu } from '@mantine/core'
import { IconPlus, IconDots, IconEdit, IconTrash, IconEye } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'

interface Tenant {
  id: string
  name: string
  description: string
  quotas: {
    cpu: number
    memory: number
    storage: number
  }
  usage: {
    cpu: number
    memory: number
    storage: number
  }
  parent: string | null
  email: string
  created_at: string
  status: string
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}

export default function Tenants() {
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    fetchTenants()
  }, [])

  const fetchTenants = async () => {
    try {
      const response = await fetch('/api/tenants')
      const data = await response.json()
      setTenants(data)
    } catch (error) {
      console.error('Failed to fetch tenants:', error)
    } finally {
      setLoading(false)
    }
  }

  const deleteTenant = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tenant?')) return

    try {
      await fetch(`/api/tenants/${id}`, { method: 'DELETE' })
      fetchTenants()
    } catch (error) {
      console.error('Failed to delete tenant:', error)
    }
  }

  const getUsagePercent = (usage: number, quota: number): number => {
    return quota > 0 ? (usage / quota) * 100 : 0
  }

  const getUsageColor = (percent: number): string => {
    if (percent >= 90) return 'red'
    if (percent >= 75) return 'yellow'
    return 'blue'
  }

  const rows = tenants.map((tenant) => {
    const cpuPercent = getUsagePercent(tenant.usage.cpu, tenant.quotas.cpu)
    const memoryPercent = getUsagePercent(tenant.usage.memory, tenant.quotas.memory)

    return (
      <Table.Tr key={tenant.id}>
        <Table.Td>
          <div>
            <Text fw={500}>{tenant.name}</Text>
            <Text size="sm" c="dimmed">{tenant.description}</Text>
          </div>
        </Table.Td>
        <Table.Td>
          <Badge color={tenant.status === 'active' ? 'green' : 'gray'}>
            {tenant.status}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Stack gap="xs">
            <div>
              <Group justify="apart" gap="xs">
                <Text size="sm">CPU: {tenant.usage.cpu.toFixed(1)} / {tenant.quotas.cpu.toFixed(1)}</Text>
                <Text size="sm" c="dimmed">{cpuPercent.toFixed(0)}%</Text>
              </Group>
              <Progress value={cpuPercent} color={getUsageColor(cpuPercent)} size="sm" />
            </div>
            <div>
              <Group justify="apart" gap="xs">
                <Text size="sm">Memory: {formatBytes(tenant.usage.memory)} / {formatBytes(tenant.quotas.memory)}</Text>
                <Text size="sm" c="dimmed">{memoryPercent.toFixed(0)}%</Text>
              </Group>
              <Progress value={memoryPercent} color={getUsageColor(memoryPercent)} size="sm" />
            </div>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{tenant.parent || '-'}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{tenant.email}</Text>
        </Table.Td>
        <Table.Td>
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <ActionIcon variant="subtle">
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEye size={14} />}
                onClick={() => navigate(`/tenants/${tenant.id}`)}
              >
                View Details
              </Menu.Item>
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                onClick={() => navigate(`/tenants/${tenant.id}/edit`)}
              >
                Edit
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={() => deleteTenant(tenant.id)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      </Table.Tr>
    )
  })

  return (
    <Stack>
      <Group justify="apart">
        <Title order={2}>Tenants</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/tenants/new')}>
          Create Tenant
        </Button>
      </Group>

      <Card>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tenant</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Resource Usage</Table.Th>
              <Table.Th>Parent</Table.Th>
              <Table.Th>Contact</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={6} style={{ textAlign: 'center' }}>
                  Loading tenants...
                </Table.Td>
              </Table.Tr>
            ) : tenants.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6} style={{ textAlign: 'center' }}>
                  No tenants found
                </Table.Td>
              </Table.Tr>
            ) : (
              rows
            )}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  )
}
